import React, { useState, useEffect } from 'react'
import * as API from './API.ts'
import NumberPicker, { NumberPickerEventDelegates } from './NumberPicker.tsx'
import Typography from 'typography'
import Spinner from './Spinner'
import ErrorMessage from './ErrorMessage'
import PropTypes from 'prop-types'

interface NumberPickerInitializerProps {
    loadParams: API.NumberPickerLoadParameters;
    delegates: NumberPickerEventDelegates;
    errorMessage?: string | null;
}

/**
 * Top-level, initialization component for the number picker functional component.
 */
const NumberPickerInitializer = (props: NumberPickerInitializerProps) => {
    const [initData, setInitData] = useState<null | API.InitResponseData>(null)
    const [error, setError] = useState<null | Error>(null)

    const initialize = async () => {
        // For analytics (so both partners and PDC can filter to sessions involving the number picker)
        if ('FS' in window) {
            window.FS.setVars('page', {
                using_pdc_number_picker: true
            })
        }

        setError(null) // clear any error (in the case where we are trying again)
        setInitData(null)
        try {
            const responseData = await API.init(props.loadParams)
            setInitData(responseData)
        } catch (error) {
            console.error('NumberPicker: an error occured while trying to initialize: ', error)
            setError(error as Error)
        }
    }

    useEffect(() => {
        initialize()
    }, [])

    if (error) {
        return (
            <ErrorMessage
                errorMessage={
                    <>
                        <Typography remoteConfigID='number_picker_init_error' />
                        <Typography component="a" remoteConfigID='number_picker_init_try_again' onClick={initialize} />
                    </>
                }
            />
        )
    }

    if (!initData) {
        return <Spinner />
    }

    return (
        <NumberPicker
            initData={initData}
            loadParams={props.loadParams}
            delegates={props.delegates}
            errorMessage={props.errorMessage}
        />
    )
}

NumberPickerInitializer.propTypes = {
    /**
     * Parameters used to initialize and load numbers.
     */
    loadParams: PropTypes.object,

    /**
     * Object containing event handlers to be delegated to certain events.
     */
    delegates: PropTypes.object,

    /**
     * Initializes the number picker with an error message.
     * Useful for integrations where the number picker is used in the context
     * of a system that helps load/initialize the number picker.
     */
    errorMessage: PropTypes.string
}

export default NumberPickerInitializer
